/**
 * ONE audited `useSyncExternalStore` store, for module-scope client state
 * several components need without prop-drilling.
 *
 * WHY THIS EXISTS AT ALL — web-pre-deploy's first cross-cutting finding.
 * This exact shape (`listeners: Set<Listener>` / `subscribe` / `getSnapshot`
 * / notify) was hand-rolled FOUR times across THREE modules — `pwa/skew.ts`,
 * `outbox/db.ts`'s counts mirror, and `outbox/clearance.ts` twice — and the
 * copies disagreed. Each one's comment cited the previous as precedent
 * (`db.ts` names `skew.ts` outright: "the same `listeners`/`subscribe`/
 * `getSnapshot` shape `pwa/skew.ts` already establishes"), so the pattern
 * propagated with its gaps intact instead of self-correcting: two of the
 * four shipped with no reset at all, and one had no equality guard. That is
 * not an aesthetic complaint — it is the measured root cause of the
 * shuffle-order failures reproduced at seeds 1, 42 and 2026 in the previous
 * cycle, where state one test latched stayed latched for whichever test ran
 * next.
 *
 * RESET IS A PROPERTY OF THE HELPER, not a thing every new store has to
 * remember to add for itself. A store created here cannot ship without one.
 *
 * THE SNAPSHOT REFERENCE IS STABLE BETWEEN `set` CALLS, and that is
 * load-bearing rather than tidy. `useSyncExternalStore` compares what
 * `getSnapshot` returns with `Object.is` to decide whether anything changed:
 * a getter that builds a fresh object on every call never returns the same
 * reference twice, so React sees "changed" on every render, calls
 * `getSnapshot` again to confirm, sees "changed" again, and spins forever.
 * `db.ts`'s counts mirror measured exactly that — every app-integration test
 * mounting `AppShell` failed with React's own "Maximum update depth
 * exceeded" — which is why `set` takes a WHOLE next value and stores it,
 * rather than exposing any way to mutate the held value in place.
 *
 * Every member is an arrow property, not a `this`-bearing method, so a
 * caller can pass them unbound straight to the hook — `useSyncExternalStore(
 * store.subscribe, store.getSnapshot)` is the intended call site, and both
 * references are stable for the module's lifetime because the store object
 * is built once at module scope.
 *
 * NOT a general-purpose state container: no selectors, no equality function,
 * no middleware. Everything this app keeps in module scope is a single small
 * value (a boolean, a status string, a two-number count), and react-query
 * remains the only server cache (CLAUDE.md) — this covers the client-only
 * half that has no server behind it at all.
 */

type Listener = () => void

export interface Store<T> {
  /** Registers `listener`; the returned teardown removes it (and only it). */
  subscribe: (listener: Listener) => () => void
  /** The currently-held value, by reference — stable until the next `set`. */
  getSnapshot: () => T
  /**
   * Replaces the held value and notifies every subscriber, UNLESS `next` is
   * already the held value by `Object.is` — the guard `pwa/skew.ts` and
   * `outbox/clearance.ts` each wrote for themselves before this helper
   * existed ("react-query, MSW and the boot/focus poll can all race to call
   * this with the SAME value ... without the guard every one of those would
   * re-notify every subscriber for no actual change"). It is inert for a
   * store holding a freshly-built object, which is the correct outcome
   * there: `db.ts`'s mirror rebuilds its snapshot precisely because a
   * count genuinely changed.
   */
  set: (next: T) => void
  /**
   * Restores the value this store was created with AND drops every
   * subscriber. Test-only in practice.
   *
   * THIS IS NOT THE PLAIN CONTRACT, and the doc here used to say it was —
   * corrected in the final-review fix wave. The seven `resetXForTests`
   * exports in `core`/`contracts` are a pure, one-way clear of a value.
   * Dropping a live listener `Set` is the SECOND contract, the one
   * web-pre-deploy item 10 found hiding under a shared name, and it is
   * precisely what `pwa/skew.ts`'s and `outbox/clearance.ts`'s exports
   * renamed themselves to `reset*AndSubscribersForTests` to stop concealing.
   * Calling the method here `reset` therefore buys brevity at the call site
   * and costs a reader the warning the rename exists to give.
   *
   * Dropping the subscribers is still deliberate: a listener left behind by
   * a component that unmounted without its teardown running would otherwise
   * go on being notified for the rest of the run, in whichever test came
   * next. But the hazard is now ONE KEYSTROKE rather than a visible
   * `listeners.clear()` in a diff — `outbox/db.ts`'s
   * `resetOutboxForTests` already needs nine lines of comment explaining why
   * it must NOT call this method. A module-level export that DOES reach this
   * must therefore say so in its own name — enforced, not merely asked for,
   * by `tools/structural/web-reset-naming.test.ts`.
   */
  reset: () => void
}

export function createStore<T>(initial: T): Store<T> {
  let value = initial
  const listeners = new Set<Listener>()

  return {
    subscribe: (listener: Listener): (() => void) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    getSnapshot: (): T => value,
    set: (next: T): void => {
      if (Object.is(value, next)) return
      value = next
      for (const listener of listeners) listener()
    },
    reset: (): void => {
      value = initial
      listeners.clear()
    },
  }
}
